import type { ReactNode } from 'react';

import {
  AlertCircle,
  ArrowUpDown,
  CalendarCheck,
  CalendarDays,
  Check,
  Minus,
  XCircle,
} from 'lucide-react';

import type { DocumentationState, RsrState } from '@/api/schema';
import { cx } from '@/lib/cx';

import { DOCUMENTATION_LABEL, labelledBy, RSR_LABEL } from '../labels';
import { HistoryTooltip, type HistoryAnchor } from './HistoryTooltip';
import styles from './StateFlag.module.css';

export type FlagTone = 'yes' | 'no' | 'progress' | 'warn' | 'danger';

/**
 * An icon and a word, in colour, with no background.
 *
 * Deliberately not a chip. A row can already carry three chips (status, Flyst stage,
 * payment) and turning the booleans into a fourth and fifth makes the row a wall of
 * pills where nothing stands out. Coloured text reads as secondary, which is what
 * these are.
 */
export function StateFlag({
  tone,
  icon,
  children,
}: {
  tone: FlagTone;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={cx(styles.flag, styles[tone])}>
      {icon}
      {children}
    </span>
  );
}

/**
 * A yes/no cell.
 *
 * False is a visible dash and the word "No", never an empty cell: an empty cell reads
 * as missing data, and "we have not contacted them" is information, not absence.
 */
export function BooleanFlag({ value }: { value: boolean }) {
  return value ? (
    <StateFlag tone="yes" icon={<Check size={14} />}>
      Yes
    </StateFlag>
  ) : (
    <StateFlag tone="no" icon={<Minus size={14} />}>
      No
    </StateFlag>
  );
}

export function DocumentationFlag({
  value,
  history,
}: {
  value: DocumentationState;
  history: HistoryAnchor;
}) {
  return (
    <HistoryTooltip
      {...history}
      field="documentation"
      label="Documentation history"
      describe={labelledBy(DOCUMENTATION_LABEL)}
    >
      {value === 'done' ? (
        <StateFlag tone="yes" icon={<Check size={14} />}>
          Done
        </StateFlag>
      ) : (
        <StateFlag tone="no" icon={<Minus size={14} />}>
          Pending
        </StateFlag>
      )}
    </HistoryTooltip>
  );
}

const RSR_FLAG: Record<RsrState, { tone: FlagTone; icon: ReactNode; label: string }> = {
  pending: { tone: 'no', icon: <Minus size={14} />, label: 'Pending' },
  arranged: { tone: 'progress', icon: <CalendarDays size={13} />, label: 'Arranged' },
  // Warn, not danger: the day has passed and somebody owes an outcome, which is a
  // prompt rather than a bad result.
  happened: { tone: 'warn', icon: <CalendarCheck size={13} />, label: 'Happened' },
  re_screening: { tone: 'progress', icon: <ArrowUpDown size={13} />, label: 'Re-screening' },
  done: { tone: 'yes', icon: <Check size={14} />, label: 'Done' },
  hold: { tone: 'warn', icon: <AlertCircle size={13} />, label: 'Hold' },
  // Not in the prototype, which falls through its default and paints a rejected
  // screening as a grey "Pending". That is a lie about a candidate's outcome, and the
  // state is real; the API stores it and can filter on it.
  rejected: { tone: 'danger', icon: <XCircle size={13} />, label: 'Rejected' },
};

/**
 * The RSR screening state.
 *
 * Note this is the *derived* state the server sends, not the stored column: a
 * screening whose date has passed with nothing recorded comes back as pending again.
 * Nothing here recomputes it.
 *
 * Which is why the card is headed "recorded" and not "history": the events are of the
 * stored column, so on a candidate whose screening date has lapsed the flag reads
 * Pending while the newest entry still says Arranged. Both are true (the state was
 * set to arranged, and the date then went by) and the two together are the
 * explanation, so long as the heading does not claim the top entry is what the flag
 * shows.
 */
export function RsrFlag({ value, history }: { value: RsrState; history: HistoryAnchor }) {
  const { tone, icon, label } = RSR_FLAG[value];
  return (
    <HistoryTooltip
      {...history}
      field="rsr_state"
      label="RSR screening, as recorded"
      describe={labelledBy(RSR_LABEL)}
    >
      <StateFlag tone={tone} icon={icon}>
        {label}
      </StateFlag>
    </HistoryTooltip>
  );
}
